import type { UserNode } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SchoolFilter,
} from '../../../filters/types/filters';
import { idsToNode, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

/**
 * Build school filter node from filter state.
 * Extracts school IDs from SchoolFilter.schoolResultItems and converts to nodes.
 */
export const buildSchoolFilter = (filters: Filter[]): UserNode | null => {
  const schoolFilter = findFilterById(filters, FilterId.SCHOOL);
  if (!schoolFilter || schoolFilter.type !== FilterType.SCHOOL) {
    return null;
  }

  const schoolResultItems = schoolFilter.schoolResultItems || {};
  const schoolIds = Object.values(schoolResultItems)
    .map((school) => Number(school.rsid))
    .filter((id): id is number => Number.isFinite(id));

  if (schoolIds.length === 0) return null;

  // School filter doesn't support current/non-current (backend will ignore curr field)
  const exprNode = idsToNode(schoolIds, TalentDiscoveryV3Tags.Rsid, true);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, schoolFilter.isNot);
};

/**
 * Build multi school filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiSchoolFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiSchool = findFilterById(filters, FilterId.MULTI_SCHOOL);

  if (!multiSchool || !isMultiFilter(multiSchool)) {
    return null;
  }

  if (multiSchool.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiSchool.instances
    .filter((inst): inst is SchoolFilter => inst?.type === FilterType.SCHOOL)
    .map((instance) => {
      const schoolResultItems = instance.schoolResultItems || {};
      const schoolIds = Object.values(schoolResultItems)
        .map((school) => Number(school.rsid))
        .filter((id): id is number => Number.isFinite(id));

      if (schoolIds.length === 0) return null;

      // School filter doesn't support current/non-current (backend will ignore curr field)
      const exprNode = idsToNode(schoolIds, TalentDiscoveryV3Tags.Rsid, true);
      if (!exprNode) return null;

      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
